import React, { useCallback, useEffect, useState } from 'react'
import useEmblaCarousel from 'embla-carousel-react'
import styled from 'styled-components'

import {
	DEFAULT_ENVIRONMENT_ID,
	environmentPhotos,
	SHOWCASE_ENVIRONMENTS_INTRO,
} from './showcaseContent'
import {
	PhotoImage,
	PhotoLayer,
	ShowcaseMainRow,
	ThumbnailDock,
	ThumbButton,
	ThumbImage,
	ThumbLabel,
	VisualColumn,
	VisualEnvironmentCard,
} from './showcaseSharedStyles'

const FullWidthVisualColumn = styled(VisualColumn)`
	flex: 1 1 100%;
	min-width: 0;
	max-width: 100%;
`

const CarouselWrap = styled.div`
	position: relative;
	align-self: stretch;
	width: 100%;
	min-height: 0;
	min-width: 0;
	flex: 1 1 auto;
`

const CarouselViewport = styled.div`
	overflow: hidden;
	width: 100%;
	height: 100%;
	touch-action: pan-y pinch-zoom;
`

const CarouselContainer = styled.div`
	display: flex;
	height: 100%;
`

const CarouselSlide = styled.div`
	flex: 0 0 100%;
	min-width: 0;
	height: 100%;
	position: relative;
`

const SlideInner = styled.div`
	position: absolute;
	inset: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	background: #0a1210;
`

const CarouselArrow = styled.button<{ $side: 'left' | 'right' }>`
	position: absolute;
	top: 50%;
	transform: translateY(-50%);
	${(p) => (p.$side === 'left' ? 'left: 10px;' : 'right: 10px;')}
	z-index: 3;
	width: 40px;
	height: 40px;
	padding: 0;
	border: none;
	border-radius: 50%;
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	background: rgba(6, 21, 18, 0.48);
	backdrop-filter: blur(12px) saturate(180%);
	-webkit-backdrop-filter: blur(12px) saturate(180%);
	box-shadow: 0 4px 18px rgba(0, 0, 0, 0.35),
		inset 0 1px 0 rgba(255, 255, 255, 0.12);
	border: 1px solid rgba(255, 255, 255, 0.16);
	color: rgba(255, 255, 255, 0.92);
	-webkit-tap-highlight-color: transparent;
	transition: background 0.2s ease, color 0.2s ease, box-shadow 0.2s ease;

	&:hover {
		background: rgba(6, 21, 18, 0.62);
		color: #fff;
	}

	svg {
		display: block;
		width: 26px;
		height: 26px;
	}
`

/** Inline SVG (avoids react-icons + @types/react JSX typing mismatch, TS2786). */
const EnvironmentsIntro = styled.p`
	position: absolute;
	top: 12px;
	left: 14px;
	z-index: 4;
	margin: 0;
	max-width: min(42ch, calc(100% - 88px));
	padding: 7px 11px;
	border-radius: 10px;
	font-family: 'GothamBook', sans-serif;
	font-size: 11px;
	line-height: 1.35;
	color: rgba(255, 255, 255, 0.94);
	background: rgba(6, 21, 18, 0.5);
	backdrop-filter: blur(10px) saturate(160%);
	-webkit-backdrop-filter: blur(10px) saturate(160%);
	border: 1px solid rgba(255, 255, 255, 0.14);
	box-shadow: 0 4px 18px rgba(0, 0, 0, 0.25);
	pointer-events: none;
`

const CarouselChevronIcon = ({ direction }: { direction: 'left' | 'right' }) => (
	<svg aria-hidden viewBox="0 0 24 24" width={26} height={26} fill="currentColor">
		{direction === 'left' ? (
			<path d="M15.41 16.59L10.83 12l4.58-4.59L14 6l-6 6 6 6 1.41-1.41z" />
		) : (
			<path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z" />
		)}
	</svg>
)

const EnvironmentShowcasePage = () => {
	const [emblaRef, emblaApi] = useEmblaCarousel({
		loop: true,
		align: 'start',
		dragFree: false,
	})

	const [selectedId, setSelectedId] = useState(DEFAULT_ENVIRONMENT_ID)

	const scrollToIndex = useCallback(
		(index: number) => {
			emblaApi?.scrollTo(index)
		},
		[emblaApi]
	)

	const scrollPrev = useCallback(() => {
		emblaApi?.scrollPrev()
	}, [emblaApi])

	const scrollNext = useCallback(() => {
		emblaApi?.scrollNext()
	}, [emblaApi])

	useEffect(() => {
		if (!emblaApi) return

		const syncFromCarousel = () => {
			const i = emblaApi.selectedScrollSnap()
			const env = environmentPhotos[i]
			if (env) setSelectedId(env.id)
		}

		const initialIndex = environmentPhotos.findIndex(
			(e) => e.id === DEFAULT_ENVIRONMENT_ID
		)
		const start = initialIndex >= 0 ? initialIndex : 0
		emblaApi.scrollTo(start, true)

		emblaApi.on('select', syncFromCarousel)
		emblaApi.on('reInit', syncFromCarousel)
		return () => {
			emblaApi.off('select', syncFromCarousel)
			emblaApi.off('reInit', syncFromCarousel)
		}
	}, [emblaApi])

	return (
		<ShowcaseMainRow>
			<FullWidthVisualColumn>
				<VisualEnvironmentCard>
					<PhotoLayer>
						<CarouselWrap>
							<EnvironmentsIntro>{SHOWCASE_ENVIRONMENTS_INTRO}</EnvironmentsIntro>
							<CarouselViewport ref={emblaRef}>
								<CarouselContainer>
									{environmentPhotos.map((env) => (
										<CarouselSlide key={env.id}>
											<SlideInner>
												<PhotoImage
													src={env.imageUrl}
													alt={`Micromart Smart Store, ${env.label}`}
												/>
											</SlideInner>
										</CarouselSlide>
									))}
								</CarouselContainer>
							</CarouselViewport>
							<CarouselArrow
								type="button"
								$side="left"
								aria-label="Previous location"
								onClick={scrollPrev}
							>
								<CarouselChevronIcon direction="left" />
							</CarouselArrow>
							<CarouselArrow
								type="button"
								$side="right"
								aria-label="Next location"
								onClick={scrollNext}
							>
								<CarouselChevronIcon direction="right" />
							</CarouselArrow>
						</CarouselWrap>
					</PhotoLayer>
					<ThumbnailDock $overlay aria-label="See it in locations, pick a scene">
						{environmentPhotos.map((env, index) => (
							<ThumbButton
								key={env.id}
								type="button"
								$glass
								$active={env.id === selectedId}
								onClick={() => scrollToIndex(index)}
							>
								<ThumbImage $compact $glass src={env.imageUrl} alt="" />
								<ThumbLabel $compact $glass>
									{env.label}
								</ThumbLabel>
							</ThumbButton>
						))}
					</ThumbnailDock>
				</VisualEnvironmentCard>
			</FullWidthVisualColumn>
		</ShowcaseMainRow>
	)
}

export default EnvironmentShowcasePage
